<script>
    import { onMount, onDestroy, tick } from "svelte";
    import * as integrationService from "../service.js";
    import { notificate } from "../../../services/notification.js";
    import { PowerTable } from "@muonw/powertable";
    import powerTableService from "../../../services/powerTable";
    import Form from "../_form.svelte";
    import { format, locale } from "svelte-i18n";
    import * as bootstrap from "bootstrap";

    import { dateText } from "../../../js/utils.js";
    import { user } from "../../user/user.state.svelte.js";

    let { data = [], onadd, ondel, onedit, ontableRefresh } = $props();

    let selected_rows = $state([]);
    let checkbox = $state();
    let tableElement = $state();
    let reload_edit_modal = $state(false);
    let ptData = $state(JSON.parse(JSON.stringify(data)));
    let postAddResponse = $state("");
    let tooltip = $state();
    let is_requesting = $state(false);
    let responseCopied = $state(false);

    // Modal Instances & Elements (matching the working example)
    let addModalElement;
    let addModalInstance;
    let editModalElement;
    let editModalInstance;
    let postAddModalElement;
    let postAddModalInstance;

    let ptInstructs = [
        { key: "select", title: "", filterable: false, sortable: false },
        { key: "name", title: $format("list.columns.name"), filterable: false },
        {
            key: "label",
            title: $format("list.columns.role"),
            filterable: false,
        },
        {
            key: "last_login_at",
            title: $format("list.columns.last_login"),
            filterable: false,
            parseAs: "unsafe-html",
        },
    ];

    let ptOptions = $state({
        uniquePrefix: "integrationTable",
        rowsPerPageOptions: [10, 20, 50, 100],
        rowsPerPage: 10,
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {},
        isDataRemote: false,
        userFunctions: { customParse: parseRows },
    });

    $effect(() => {
        if ($locale) {
            ptOptions.translations = {
                add: $format("list.interface.add"),
                delete: $format("list.interface.delete"),
                filterBy: $format("list.interface.filterBy"),
                from: $format("list.interface.from"),
                invertSelection: $format("list.interface.invertSelection"),
                next: $format("list.interface.next"),
                of: $format("list.interface.of"),
                previous: $format("list.interface.previous"),
                rows: $format("list.interface.rows"),
                search: $format("list.interface.search"),
                selectAll: $format("list.interface.selectAll"),
                selectNone: $format("list.interface.selectNone"),
            };
        }
    });

    // --- NEW MODAL CONTROLLERS ---

    const openAddModal = (e) => {
        if (e) e.preventDefault();
        addModalInstance.show();
    };

    const closeAddModal = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        addModalInstance.hide();
    };

    const handleEdit = async () => {
        if (selected_rows.length < 1)
            return notificate("select_element", "modal");
        if (selected_rows.length > 1)
            return notificate("select_only_one", "modal");
        reload_edit_modal = !reload_edit_modal; // Trashes form state cleanly
        await tick();
        editModalInstance.show();
    };

    const closeEditModal = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        editModalInstance.hide();
    };

    const handleAdd = async (integration) => {
        postAddResponse = integration.secret;

        closeAddModal();
        closeEditModal();

        await tick();

        setTimeout(() => {
            postAddModalInstance.show();
        }, 400);
    };

    const handleEditFn = async (integration) => {
        if (onedit) onedit(integration);
        closeAddModal();
        closeEditModal();
    };

    const closeResponseModal = async () => {
        if (!responseCopied) {
            let result = await notificate("confirm_secret_closure", "modal", {
                fileType: null,
            });
            if (!result.isConfirmed) return false;
        }
        postAddModalInstance.hide();
        setTimeout(() => {
            if (onadd) onadd();
        }, 350);
    };

    // --- REST OF FUNCTIONS ---

    function parseRows(pageContent) {
        pageContent.forEach((row) => {
            if (row["last_login_at"]) {
                let last_login_at = row.last_login_at;
                row["last_login_at"] =
                    `<span title="${last_login_at}">${dateText(row["last_login_at"])}</span>`;
            }
        });
        deselectAll();
        return pageContent;
    }

    let handleDelete = async () => {
        let result = await notificate("confirm_delete", "modal", {
            massive: true,
            findings_warning: false,
        });
        if (!result.isConfirmed) return false;

        is_requesting = true;
        let ids = selected_rows.map((row) => row.id);
        let integrationsToDelete = data.filter((integration) =>
            ids.includes(integration.id),
        );

        integrationsToDelete.forEach((integration, i) => {
            integrationService
                .del(integration)
                .then(() => {
                    if (i == integrationsToDelete.length - 1) {
                        is_requesting = false;
                        if (ondel) ondel(integration);
                    }
                })
                .catch((errors) => {
                    is_requesting = false;
                    if (ondel) ondel(integration);
                    return errors;
                });
        });
    };

    const selectAll = async (e) => {
        let tData = tableElement.getData(false, ["pageData", "matchedData"]);
        let pageData = tData.pageData;
        if (!pageData || pageData.length === 0) return selected_rows;

        const selectedIds = new Set(selected_rows.map((r) => r.id));
        const allPageSelected = pageData.every((el) => selectedIds.has(el.id));
        let action = allPageSelected ? "deselect" : "select";

        if (action === "select") {
            let result;
            if (tData.matchedData.length > 10) {
                result = await notificate("select_all", "modal", {});
                if (!result.isConfirmed && result.dismiss !== "cancel")
                    return selected_rows;
            }

            let target_elements =
                result && result.isConfirmed ? tData.matchedData : pageData;
            const currentIds = new Set(selected_rows.map((x) => x.id));
            let toAdd = target_elements.filter((x) => !currentIds.has(x.id));
            selected_rows = [...selected_rows, ...toAdd];
        } else {
            selected_rows = [];
        }

        powerTableService.toggleActionButtons(selected_rows);
        const domRows = document.querySelectorAll(
            `div[data-prefix='integrationTable'] table tbody tr`,
        );
        const selectedPtIds = new Set(
            selected_rows.map((r) => String(r.__PT_ID__)),
        );

        domRows.forEach((row) => {
            if (selectedPtIds.has(row.getAttribute("data-id")))
                row.classList.add("selected");
            else row.classList.remove("selected");
        });

        return selected_rows;
    };

    const deselectAll = () => {
        selected_rows = powerTableService.deselectAll();
        powerTableService.toggleActionButtons(selected_rows);
        return selected_rows;
    };

    const selectAllEventListener = () => {
        checkbox = document.querySelector(
            "th[data-key='select'][data-sortable='false']",
        );
        if (checkbox) checkbox.addEventListener("click", selectAll);
    };

    const selectRow = (event) => {
        let tData = tableElement.getData(false, ["pageData"]);
        let current = event.detail.data;
        let idx = selected_rows.findIndex((r) => r.id == current.id);
        selected_rows = powerTableService.selectRows(
            selected_rows,
            current,
            idx,
            "integrationTable",
            tData.pageData,
        );
        powerTableService.toggleActionButtons(selected_rows);
        return selected_rows;
    };

    const exportToJSON = (e) => {
        let currentData = tableElement.getData(
            false,
            "matchedData",
        ).matchedData;
        for (let item of currentData) {
            delete item.__PT_ID__;
            delete item.__PT_CB__;
            delete item.is_enabled;
            delete item.workspace_id;
            delete item.id;
        }

        let dataStr =
            "data:text/json;charset=utf-8," +
            encodeURIComponent(JSON.stringify(currentData));
        let downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute(
            "download",
            `Integraciones_vulseek.json`,
        );
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    const exportToCSV = async () => {
        let header = [];
        let titles = [];
        const tData = tableElement.getData(false, "matchedData").matchedData;
        let items = JSON.parse(JSON.stringify(tData));
        const replacer = (key, value) => (value === null ? "" : value);

        ptInstructs.forEach((col) => {
            if (col.key != "select")
                header.push({ key: col.key, value: col.title });
            if (col.title.includes(" "))
                col.title = col.title.replace(" ", "_");
        });

        const csv = [
            titles.join(";"),
            ...items.map((row) =>
                header
                    .map((fieldName) =>
                        JSON.stringify(row[fieldName["key"]], replacer),
                    )
                    .join(";"),
            ),
        ].join("\r\n");

        let hiddenElement = document.createElement("a");
        hiddenElement.href =
            "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
        hiddenElement.target = "_blank";
        hiddenElement.download = `Integraciones_vulseek.csv`;
        hiddenElement.click();
    };

    const handleCopy = async (str, tooltipRef) => {
        try {
            // Modern, native way to copy text (returns a Promise)
            await navigator.clipboard.writeText(str);

            // Handle UI updates
            showTooltip(tooltipRef);
            responseCopied = true;

            // Add a delay before hiding so the user actually sees it
            setTimeout(() => {
                hideTooltip(tooltipRef);
            }, 2000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    const hideTooltip = (tooltipRef) => {
        setTimeout(() => {
            tooltipRef.classList.add("trigger-opacity-cero");
            tooltipRef.classList.remove("trigger-opacity-one");
            setTimeout(() => tooltipRef.classList.add("d-none"), 1000);
        }, 2000);
    };

    const showTooltip = (tooltipRef) => {
        tooltipRef.classList.remove("d-none");
        setTimeout(() => {
            tooltipRef.classList.remove("trigger-opacity-cero");
            tooltipRef.classList.add("trigger-opacity-one");
        }, 1000);
    };

    onMount(async () => {
        // Init Modals using Bootstrap JS and Svelte bindings!
        addModalInstance = new bootstrap.Modal(addModalElement);
        editModalInstance = new bootstrap.Modal(editModalElement);
        postAddModalInstance = new bootstrap.Modal(postAddModalElement);

        await tick();
        tooltip = document.getElementById("tooltip-response");
        selectAllEventListener();
    });

    onDestroy(() => {
        if (checkbox) checkbox.removeEventListener("click", selectAll);

        // Clean up to be safe
        document
            .querySelectorAll(".modal-backdrop")
            .forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("overflow");
    });
</script>

<div class="row">
    <div class="col-12">
        <div class="card">
            <div class="card-header">
                <div class="float-end">
                    {#if user.data.role != "viewer"}
                        <button
                            class="btn btn-secondary table-button btn-add btn-xs t-add-integration-btn"
                            tabindex="0"
                            aria-controls="integrationTable"
                            type="button"
                            title={$format("buttons.add")}
                            id="integrationAddBtn"
                            onclick={openAddModal}
                            ><i class="fa fa-plus"></i></button
                        >

                        <button
                            disabled
                            class="btn btn-secondary btn-disabled table-button btn-delete btn-xs t-delete-integration-btn"
                            tabindex="0"
                            aria-controls="integrationTable"
                            type="button"
                            title={$format("buttons.delete")}
                            id="integrationDeleteBtn"
                            onclick={handleDelete}
                            ><i class="fa fa-trash"></i></button
                        >

                        <button
                            disabled
                            class="btn btn-secondary btn-disabled table-button btn-edit btn-xs t-edit-integration-btn"
                            tabindex="0"
                            aria-controls="integrationTable"
                            type="button"
                            title={$format("buttons.edit")}
                            id="integrationEditBtn"
                            onclick={handleEdit}
                            ><i class="fa fa-edit"></i></button
                        >
                    {/if}
                    <button
                        class="btn btn-secondary table-button btn-refresh btn-xs t-refresh-integration-btn"
                        tabindex="0"
                        aria-controls="integrationTable"
                        type="button"
                        title={$format("buttons.refresh")}
                        id="integrationRefreshBtn"
                        onclick={() => {
                            if (ontableRefresh) ontableRefresh();
                        }}><i class="fa fa-sync-alt"></i></button
                    >

                    <button
                        class="btn btn-secondary table-button btn-export btn-xs t-json-integration-btn"
                        tabindex="0"
                        aria-controls="integrationTable"
                        type="button"
                        title={$format("buttons.json")}
                        id="integrationJSONbtn"
                        onclick={exportToJSON}
                        ><i class="fab fa-js-square" style="font-size: 18px;"
                        ></i></button
                    >

                    <button
                        class="btn btn-secondary table-button btn-export btn-xs t-csv-integration-btn"
                        tabindex="0"
                        aria-controls="integrationTable"
                        type="button"
                        title={$format("buttons.csv")}
                        id="integrationCSVbtn"
                        onclick={exportToCSV}
                        ><i class="fa fa-file-csv" style="font-size: 18px;"
                        ></i></button
                    >
                </div>
            </div>
            <div class="card-body">
                <div class="table-responsive">
                    <div class="overlay-wrapper">
                        <div class="text-center" class:overlay={is_requesting}>
                            <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                        </div>
                    </div>
                    <PowerTable
                        {ptData}
                        {ptOptions}
                        {ptInstructs}
                        on:rowClicked={(e) => selectRow(e)}
                        bind:this={tableElement}
                    >
                        <div slot="noResults">
                            {$format("results_not_found")}
                        </div>
                    </PowerTable>
                </div>
            </div>
        </div>
    </div>
</div>

<!-- ADD MODAL -->
<div
    class="modal fade"
    id="integration-add-modal"
    tabindex="-1"
    aria-hidden="true"
    bind:this={addModalElement}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("add")} integración</h4>
                <button
                    type="button"
                    class="btn-close"
                    onclick={closeAddModal}
                    aria-label="Close"
                ></button>
            </div>
            <div class="modal-body">
                <Form onsave={handleAdd} oncancel={closeAddModal} />
            </div>
        </div>
    </div>
</div>

<!-- EDIT MODAL -->
<div
    class="modal fade"
    id="integration-edit-modal"
    tabindex="-1"
    aria-hidden="true"
    bind:this={editModalElement}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Editar Integración</h4>
                <button
                    type="button"
                    class="btn-close"
                    onclick={closeEditModal}
                    aria-label="Close"
                ></button>
            </div>
            <div class="modal-body">
                {#key reload_edit_modal}
                    <Form
                        integration={selected_rows[0]}
                        onsave={handleEditFn}
                        oncancel={closeEditModal}
                    />
                {/key}
            </div>
        </div>
    </div>
</div>

<!-- POSTADD MODAL -->
<div
    class="modal fade"
    id="postAdd-modal"
    tabindex="-1"
    data-bs-keyboard="false"
    data-bs-backdrop="static"
    aria-hidden="true"
    bind:this={postAddModalElement}
>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Integración agregada</h4>
                <button
                    type="button"
                    class="btn-close"
                    onclick={closeResponseModal}
                    aria-label="Close"
                ></button>
            </div>
            <div class="modal-body">
                <button
                    class="btn btn-copy far fa-copy"
                    title="Copiar al portapapeles"
                    onclick={() => handleCopy(postAddResponse, tooltip)}
                ></button>
                <span
                    id="tooltip-response"
                    class="d-none trigger-opacity-cero copy-tooltip"
                    >Copiado</span
                >

                {#key postAddResponse}
                    <div class="console">
                        {postAddResponse}
                    </div>
                {/key}

                <div class="alert alert-warning mt-3" role="alert">
                    Incorporá el secreto al archivo correspondiente. Este
                    secreto no puede ser recuperado en el futuro.
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    .alert.alert-warning {
        color: #856404;
        background-color: #fff3cd;
    }
    .console {
        background: hsl(0, 0%, 96.5%);
        color: black-750;
        font-family: "Lucida Console", "Courier New", monospace;
        padding: 15px;
    }
    .btn-copy {
        float: right;
        position: relative;
        top: 5px;
        right: 5px;
    }
    #tooltip-response {
        position: absolute;
        right: -60px;
        top: 22px;
    }
    .overlay-wrapper div {
        display: none;
    }
    .overlay-wrapper div.overlay {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-top: 20px;
    }
</style>
